import { useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { KPI } from '@/components/ui/kpi';
import { Eyebrow } from '@/components/ui/eyebrow';
import {
  Plus,
  Upload,
  Receipt,
  Clock,
  CheckCircle2,
  Sparkles,
  Search,
} from 'lucide-react';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import { useAuth } from '@/lib/auth';
import {
  usePagamentos,
  usePagamentosAggregate,
  type PagamentoStatus,
} from '@/lib/hooks/pagamentos';
import { PagamentoDrawer } from '@/features/pagamentos/PagamentoDrawer';
import { PagamentosTable } from '@/features/pagamentos/PagamentosTable';

export const Route = createFileRoute('/_app/lancamentos/pagamentos')({
  component: PagamentosPage,
});

function currentCompetencia(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

const MES_NOMES: Record<string, string> = {
  '01': 'janeiro', '02': 'fevereiro', '03': 'março', '04': 'abril',
  '05': 'maio', '06': 'junho', '07': 'julho', '08': 'agosto',
  '09': 'setembro', '10': 'outubro', '11': 'novembro', '12': 'dezembro',
};

const PAGE_SIZE = 25;

function PagamentosPage() {
  const { user } = useAuth();
  const workspaceId = user?.effectiveWorkspaceId ?? null;

  const [competencia, setCompetencia] = useState(currentCompetencia());
  const [status, setStatus] = useState<PagamentoStatus | 'ALL'>('ALL');
  const [search, setSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const filters = useMemo(
    () => ({
      competencia,
      status: status === 'ALL' ? undefined : status,
      search: search.trim() || undefined,
      limit: PAGE_SIZE,
      offset,
    }),
    [competencia, status, search, offset],
  );

  const listQ = usePagamentos(workspaceId, filters);
  const aggQ = usePagamentosAggregate(workspaceId, competencia);

  const items = listQ.data?.items ?? [];
  const total = listQ.data?.total ?? 0;

  const agg = aggQ.data;
  const totalLanc = agg?.totalLancamentos ?? 0;
  const conciliados = agg?.conciliado.count ?? 0;
  const pctConciliado = totalLanc > 0 ? Math.round((conciliados / totalLanc) * 100) : 0;

  const mesNome = competencia ? MES_NOMES[competencia.slice(5, 7)] ?? '' : '';

  if (!workspaceId) {
    return (
      <Card variant="default" className="p-8 text-center">
        <p className="text-sm text-fg-muted">
          Você está logado como administrador da plataforma. Selecione um workspace para gerenciar pagamentos.
        </p>
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <Eyebrow>Lançamento · Mensal</Eyebrow>
          <h1 className="font-display text-[34px] md:text-[42px] font-bold tracking-[-0.03em] text-fg leading-[1.0] mt-1">
            Pagamentos <span className="text-fg-muted font-normal">· {mesNome}</span>
          </h1>
          <p className="text-sm text-fg-muted mt-2">Despesas pagas com recursos do convênio</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="default" size="lg" disabled>
            <Upload className="h-4 w-4" strokeWidth={1.8} />
            Importar planilha
          </Button>
          <Button variant="primary" size="lg" onClick={() => setDrawerOpen(true)}>
            <Plus className="h-4 w-4" strokeWidth={1.8} />
            Novo pagamento
          </Button>
        </div>
      </header>

      <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <KPI
          tone="mint"
          label="Total pago no mês"
          value={fmtCurrency(agg?.conciliado.total ?? '0')}
          delta={`${conciliados} ${conciliados === 1 ? 'conciliado' : 'conciliados'}`}
          icon={Receipt}
        />
        <KPI
          label="A pagar (pendente)"
          value={fmtCurrency(agg?.pendente.total ?? '0')}
          delta={`${agg?.pendente.count ?? 0} ${(agg?.pendente.count ?? 0) === 1 ? 'item' : 'itens'}`}
          icon={Clock}
        />
        <KPI
          label="Conciliado"
          value={totalLanc > 0 ? `${conciliados} de ${totalLanc}` : '—'}
          delta={totalLanc > 0 ? `${pctConciliado}%` : 'sem extrato'}
          deltaDir={conciliados > 0 ? 'up' : undefined}
          icon={CheckCircle2}
        />
        <KPI
          tone="ink"
          label="Custo IA · pgmts"
          value="R$ 0,00"
          delta="0 OCRs"
          icon={Sparkles}
        />
      </section>

      <Card variant="default" className="p-4">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-fg-muted" strokeWidth={1.8} />
            <Input
              placeholder="Buscar por credor…"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setOffset(0);
              }}
              className="pl-9"
            />
          </div>
          <Input
            type="month"
            value={competencia}
            onChange={(e) => {
              setCompetencia(e.target.value);
              setOffset(0);
            }}
            className="w-[160px]"
          />
          <Select
            value={status}
            onValueChange={(v) => {
              setStatus(v as PagamentoStatus | 'ALL');
              setOffset(0);
            }}
          >
            <SelectTrigger className="w-[160px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Todos os status</SelectItem>
              <SelectItem value="PENDENTE">Pendente</SelectItem>
              <SelectItem value="CONCILIADO">Conciliado</SelectItem>
              <SelectItem value="CONTESTADO">Contestado</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Card>

      <PagamentosTable
        workspaceId={workspaceId}
        data={items}
        total={total}
        limit={PAGE_SIZE}
        offset={offset}
        loading={listQ.isLoading}
        onPageChange={setOffset}
      />

      <PagamentoDrawer
        workspaceId={workspaceId}
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        defaultCompetencia={competencia}
      />
    </div>
  );
}

function fmtCurrency(value: string): string {
  const n = Number(value);
  if (Number.isNaN(n)) return 'R$ 0,00';
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(n);
}
